"use client";
import { Modal } from "@components/atoms/Modal";
import Button from "@components/atoms/Button";
import styles from "./IntentModal.module.scss";
import ColorButton from "../ColorSwitchButton";
import { postLead } from "@services/leadCaptureService";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { useAppSelector, useAppDispatch } from "@lib/redux/hooks";
import { updateProfileAsync } from "@lib/redux/slices/profileSlice";
import { useState, useEffect } from "react";
import { PageType } from "@constants/pageTypes";
import { usePathname } from "next/navigation";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getPlatform } from "@utils/platformDetection.client";
import { getCarAvailability } from "@utils/carAvailability";
import { getLeadData, setLeadData } from "@utils/leadDataStorage";

interface CarDetails {
  name: string;
  color: {
    name: string;
    hex: string[];
    type: string;
  };
}

interface IntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  carDetails?: CarDetails;
  onIntentSelect?: (intent: string) => void;
  onRequestCallback?: (intent: string) => void;
  onGetQuote?: (intent: string) => void;
  mmvDetails?: any;
  buyingIntent: any;
  cityName: string;
  highPriorityCapture?: boolean;
  variant?: "full" | "simple"; // New prop for variant
  pageType?: PageType | null;
  loginType?: string | null;
}

const IntentModal = ({
  isOpen,
  onClose,
  carDetails,
  onIntentSelect,
  onRequestCallback,
  onGetQuote,
  buyingIntent,
  cityName,
  mmvDetails,
  highPriorityCapture,
  variant = "full", // Default to full variant
  pageType,
  loginType,
}: IntentModalProps) => {
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);
  // Access Redux state directly
  const userProfile = useAppSelector((state) => state.profile.data);
  const dispatch = useAppDispatch();

  // Derive selectedIntent from userProfile buying_period
  const [selectedIntent, setSelectedIntent] = useState<string>("");

  useEffect(() => {
    const buyingPeriod = userProfile?.buying_period || "";
    setSelectedIntent(buyingPeriod);
  }, [userProfile?.buying_period]);

  const contactNo = userProfile?.contact_no || userProfile?.phone || "";
  const buyingPeriod = userProfile?.buying_period || "";

  const { buying_intent: intentData } = buyingIntent || {};
  let intentQuestion = "";
  let intentOptions = [];

  if (
    buyingPeriod?.length > 0 &&
    buyingPeriod !== "Not Asked" &&
    buyingPeriod !== "None"
  ) {
    intentOptions = intentData?.secondary_popup?.option1 || [];
    intentQuestion = intentData?.secondary_popup?.question;
  } else {
    intentOptions = intentData?.primary_popup?.option1 || [];
    intentQuestion = intentData?.primary_popup?.question;
  }

  const handleIntentSelection = async (selectedValue: string) => {

    let leadData: any = {
        buying_period: selectedValue,
        city: cityName,
        phone: contactNo,
      };
      if (!!mmvDetails) {
        leadData = {
          ...leadData,
          make: mmvDetails?.make || "",
          model: mmvDetails?.model || "",
          variant: mmvDetails?.variant || "",
          color: mmvDetails?.color || "",
        };
      }
      const utmTerm = new URL(window.location.href).searchParams.get("utm_term") || "";
       leadData.dump_data = {
          mx_Platform: getPlatform(),
          mx_Page_Type_Web: pageType,
          mx_Login_Type: loginType || sessionStorage.getItem("loginType") ,
          // SourceReferrerURL: sessionStorage.getItem("referrerSource"),
          mx_URL: window.location.href || "https://ackodrive.com",
          mx_UTM_Term: utmTerm,
          mx_color: leadData?.color || "",
       };
    // Get existing lead data with fallback to localStorage
    const existingLeadData = getLeadData();
    const mergedLeadData = { ...existingLeadData, ...leadData };
    leadData = mergedLeadData;
    
    // Save to both storages
    setLeadData(mergedLeadData);
    const fetchData = {
      qualification_update_data: {
        buying_period: selectedValue,
      },
    };
    const buyingPeriod = selectedValue || "";
    try {
      const [leadResponse, profileUpdateResult] = await Promise.all([
        postLead(addUTMParamsToLeadData(leadData)),
        dispatch(
          updateProfileAsync({
            profileUpdates: { buying_period: selectedValue },
            apiPayload: fetchData,
          })
        ),
      ]);

      if (
        leadResponse.success &&
        updateProfileAsync.fulfilled.match(profileUpdateResult)
      ) {
        // Save merged lead data
        setLeadData(mergedLeadData);
        
        const leadCreated = !!leadResponse?.data?.is_created;
        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
          pageType: pageType || "",
          additionalProps: {
            action_type: leadCreated ? "lead_created" : "lead_updated",
            // car_availability: carAvailability,
            vehicle_make: mmvDetails?.make,
            vehicle_model: mmvDetails?.model,
            vehicle_variant: mmvDetails?.variant,
            vehicle_color: mmvDetails?.color || "",
            city_name: cityName,
            login_type: loginType || sessionStorage.getItem("loginType"),
            intent: buyingPeriod,
          },
        });
        sessionStorage.setItem("buying_intent", "false");
      } else if (!leadResponse.success) {
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType || "",
          additionalProps: {
            exception_type: "API issue",
            exception_name: "failed lead creation",
            vehicle_make: mmvDetails?.make,
            vehicle_model: mmvDetails?.model,
            vehicle_variant: mmvDetails?.variant,
            vehicle_color: mmvDetails?.color || "",
            city_name: cityName,
            login_type: loginType || sessionStorage.getItem("loginType"),
            intent: buyingPeriod,
          },
        });
      }
    } catch (error) {
      console.error("Error in postLead API call:", error);
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType: pageType || "",
        additionalProps: {
          exception_type: "API issue",
          exception_name: "failed lead creation",
          vehicle_make: mmvDetails?.make,
          vehicle_model: mmvDetails?.model,
          vehicle_variant: mmvDetails?.variant,
          vehicle_color: mmvDetails?.color || "",
          city_name: cityName,
          login_type: loginType || sessionStorage.getItem("loginType"),
          intent: buyingPeriod,
        },
      });
    }
  };

  const handleChipSelect = async (intent: string) => {
    setSelectedIntent(intent);
    onIntentSelect?.(intent);

    // For simple variant, auto-execute on selection and close
    if (variant === "simple") {
      handleIntentSelection(intent);
      onGetQuote?.(intent);
      onClose();
    }
  };

  const handleRequestCallback = () => {
    handleIntentSelection(selectedIntent);
    onRequestCallback?.(selectedIntent);
    onClose();
  };

  const handleGetQuote = () => {
    showLoader("Loading Quote...");
    handleIntentSelection(selectedIntent);
    onGetQuote?.(selectedIntent);
    onClose();
  };

  // Custom close handler that only allows closing if a selection was made
  const handleModalClose = () => {
    // For simple variant, don't allow manual close - only through selection
    if (variant === "simple") {
      return;
    }

    // For full variant, only allow close if an intent is selected
    if (selectedIntent) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={intentQuestion}
      className={styles.intentModal}
      showCloseButton={variant === "full" && !!selectedIntent}
    >
      <div className={styles.modalContent}>
        {/* Car Details Section - Only show in full variant */}
        {variant === "full" && (
          <div className={styles.carDetailsSection}>
            <div className={styles.carName}>
              {carDetails?.name || "Car Name"}
            </div>
            <div className={styles.colorSelection}>
              <div className={styles.colorInfo}>
                <ColorButton
                  color={{
                    name: carDetails?.color?.name || "",
                    color: carDetails?.color?.hex || [],
                    type: carDetails?.color?.type || "",
                  }}
                  isActive={false}
                  brandName={mmvDetails?.make}
                  modelName={mmvDetails?.model}
                  variantName={mmvDetails?.variant}
                  pageType={pageType || ""}
                  carAvailability={getCarAvailability(mmvDetails?.status)}
                />
                <span className={styles.colorName}>
                  {carDetails?.color?.name || "Colour Name"}
                </span>
              </div>
              <button
                className={styles.changeButton}
                onClick={handleModalClose}
              >
                Change
              </button>
            </div>
          </div>
        )}

        {/* Intent Selection Chips */}
        <div className={styles.intentChips}>
          {intentOptions?.map(
            (
              option: {
                value: string;
                createLead: string;
              },
              idx: number
            ) => (
              <button
                key={`${option}-${idx}`}
                className={`${styles.chip} ${selectedIntent?.replace(/\s/g, "") ===
                    option?.value?.replace(/\s/g, "")
                    ? styles.selected
                    : ""
                  }`}
                onClick={() => handleChipSelect(option?.value)}
              >
                {option?.value}
              </button>
            )
          )}
        </div>

        {/* Action Buttons - Only show in full variant */}
        {variant === "full" && (
          <div className={styles.actionButtons}>
            <Button
              className={`${styles.callbackButton} ${!selectedIntent ? styles.disabled : ""
                }`}
              onClick={handleRequestCallback}
              disabled={!selectedIntent}
            >
              Request&nbsp;callback
            </Button>
            <Button
              className={`${styles.quoteButton} ${!selectedIntent ? styles.disabled : ""
                }`}
              onClick={handleGetQuote}
              disabled={!selectedIntent}
            >
              Get quote
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default IntentModal;
